import React, {
  ReactNode,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import deepEqual from 'react-fast-compare';
import styled from 'styled-components';

import Box from 'components/Box';
import Text, { BodySmall } from 'components/Text';
import { identity, isDefined } from 'utils/functional';
import { camelCaseToDash } from 'utils/string';

import ErrorIcon from 'error.svg';

import { breakpoints, colors, textStyles, transition } from '../../theme';
import { FormContextConsumer } from './FormContext';

export const getQaId = (name: string) =>
  camelCaseToDash(name).replace(/\./g, '-');
const FIELD_MARGIN = 30;
const BaseLabel = styled.label<{ noWrap?: boolean; for?: string }>`
  ${textStyles.h5};
  ${(props) =>
    props.noWrap
      ? `
  @media (min-width: ${breakpoints[2]}) {
    white-space: nowrap;
    max-width: 560px;
  }`
      : ''}
`;
export const Label = styled(BaseLabel)`
  & + * {
    margin-top: 16px;
  }
`;
const InlineLabel = styled(BaseLabel)<{
  hasFocus?: boolean;
  hasValue?: boolean;
}>`
  position: absolute;
  top: 50%;
  left: 18px;
  transform: translateY(-50%);
  pointer-events: none;
  transform-origin: left top;
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.4px;
  transition: all ${transition};
  ${(props) =>
    (props.hasFocus || props.hasValue) &&
    `
      transform: scale(0.625) translateY(-50%) translateY(-28px);
      opacity: 0.8;
    `}
`;
export const Description = styled(BodySmall)<{ $noWrap?: boolean }>`
  margin-top: 0;
  color: ${colors.battleshipGrey};
  line-height: 1.375;
  ${(props) =>
    props.$noWrap
      ? `
  @media (min-width: ${breakpoints[2]}) {
    display: flex;
    & > div {
      flex: 1 0 auto;
      max-width: 720px;
    }
  }`
      : ''}
  + * {
    margin-top: 16px;
  }
`;
export const ErrorContainer = styled.div<{
  inlineLabel?: boolean;
  noWrap?: boolean;
}>`
  display: flex;
  margin-top: 4px;
  font-size: 13px;
  color: ${colors.scarlet};
  ${(props) =>
    props.inlineLabel &&
    `
    position: absolute;
    top: 50%;
    right: 18px;
    transform: translateY(-50%);
    margin-top: 0;
  `}
  ${(props) =>
    props.noWrap
      ? `
  @media (min-width: ${breakpoints[2]}) {
    white-space: nowrap;
    max-width: 560px;
  }`
      : ''}
  svg {
    margin-top: 3px;
  }
`;
// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
export const Error = ({
  errorMessage,
  inlineLabel,
  noWrap,
  qaId,
  ...props
}: {
  errorMessage: ReactNode;
  inlineLabel?: boolean;
  noWrap?: boolean;
  qaId?: string;
}) => (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/jsx-props-no-spreading
  <ErrorContainer noWrap={noWrap} inlineLabel={inlineLabel} {...props}>
    <ErrorIcon />
    <Text pl="4px" data-qa-id={qaId ? `${qaId}-error` : undefined}>
      {errorMessage}
    </Text>
  </ErrorContainer>
);
export const FieldBox = styled(Box)`
  margin-bottom: ${FIELD_MARGIN}px;
`;
type Props<I> = {
  name: string;
  label?: ReactNode;
  inlineLabel?: boolean;
  hidden?: boolean;
  description?: ReactNode;
  renderInput: (arg0: {
    fieldQaIdPrefix: string;
    labelRef: {
      // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'React$ElementRef'.
      current: null | React$ElementRef<'label'>;
    };
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: Record<string, any>;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    id: string;
    qaId: string;
    errors: APIErrors;
    hasError: boolean;
    hasSuccess: boolean;
    hasGeneralError: boolean;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValue: (arg0: any) => void;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValueWith: (arg0: (...args: Array<any>) => any) => void;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValueByName: (arg0: string, arg1: any) => void;
    getValue: (arg0: string) => unknown;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur?: (...args: Array<any>) => any;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFocus?: (...args: Array<any>) => any;
  }) => ReactNode;
  mapError?: (arg0: string) => ReactNode;
  matchGeneralError?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputProps?: Record<string, any>;
  customCompare?: (arg0: I, arg1: I) => boolean;
  customErrorMatch?: (
    arg0: APIErrors
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Record<string, any> | null | undefined;
  noMargin?: boolean;
  noWrap?: boolean;
  success?: boolean;
  customError?: string | null | undefined;
};

const checkDeep = (a: unknown, b: unknown): boolean =>
  a === b || deepEqual(a, b);

const areEqual = (pp, np) => {
  const customComparison = np.customCompare ? np.customCompare(pp, np) : true;

  if (pp.name !== np.name) {
    return false;
  }

  const value = np.getValue(np.name);
  const prevValue = pp.getValue(pp.name);
  const deepChecks = ['errors', 'inputProps'];
  return (
    np.label === pp.label &&
    np.customError === pp.customError &&
    np.success === pp.success &&
    customComparison &&
    deepChecks.every((p) => checkDeep(pp[p], np[p])) &&
    checkDeep(value, prevValue)
  );
};

type FieldInternalProps = {
  getValue: (arg0: string) => unknown;
  fieldQaIdPrefix: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>;
  labelRef: {
    // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'React$ElementRef'.
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    current: null | React$ElementRef<'label'> | ((...args: Array<any>) => any);
  };
  updateValue: (arg0: string, arg1: unknown) => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateValueWith: (arg0: string, arg1: (...args: Array<any>) => any) => void;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onFocus: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBlur: (...args: Array<any>) => any;
  errors: APIErrors;
  FieldLabel: React.ComponentType<{
    htmlFor: string;
    id: string;
    inlineLabel?: boolean;
    hasFocus: boolean;
    hasValue: boolean;
    noWrap?: boolean;
  }>;
  hasFocus: boolean;
  name: string;
  label?: ReactNode;
  inlineLabel?: boolean;
  hidden?: boolean;
  description?: ReactNode;
  renderInput: (arg0: {
    fieldQaIdPrefix: string;
    labelRef: {
      // @ts-expect-error ts-migrate(2304) FIXME: Cannot find name 'React$ElementRef'.
      current: null | React$ElementRef<'label'>;
    };
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: Record<string, any>;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any;
    id: string;
    qaId: string;
    errors: APIErrors;
    hasError: boolean;
    hasSuccess: boolean;
    hasGeneralError: boolean;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValue: (arg0: any) => void;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValueWith: (arg0: (...args: Array<any>) => any) => void;
    getValue: (arg0: string) => unknown;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    updateValueByName: (arg0: string, arg1: any) => void;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur?: (...args: Array<any>) => any;
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFocus?: (...args: Array<any>) => any;
  }) => ReactNode;
  mapError: (arg0: string) => ReactNode;
  matchGeneralError?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inputProps?: Record<string, any>;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line react/no-unused-prop-types
  customCompare?: (
    arg0: FieldInternalProps,
    arg1: FieldInternalProps
  ) => boolean;
  customErrorMatch?: (
    arg0: APIErrors
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Record<string, any> | null | undefined;
  noMargin?: boolean;
  noWrap?: boolean;
  success?: boolean;
  customError?: string | null | undefined;
};
const FieldInternal = memo(
  ({
    getValue,
    fieldQaIdPrefix,
    renderInput,
    values,
    labelRef,
    errors,
    updateValue,
    updateValueWith,
    onFocus,
    onBlur,
    inlineLabel,
    hidden,
    name,
    label,
    description,
    matchGeneralError,
    FieldLabel,
    hasFocus,
    mapError,
    customErrorMatch,
    noMargin,
    noWrap,
    inputProps,
    success,
    customError,
  }: FieldInternalProps) => {
    const qaId = fieldQaIdPrefix
      ? `${fieldQaIdPrefix}-${getQaId(name)}`
      : getQaId(name);
    const id = qaId;
    const generalError = errors[''] || errors.general;
    const hasGeneralError =
      isDefined(matchGeneralError) && generalError === matchGeneralError;
    const value = getValue(name);
    const inputPropsValue = inputProps && inputProps.value;
    useEffect(() => {
      if (inputPropsValue && inputPropsValue !== value) {
        updateValue(name, inputPropsValue);
      }
    }, [inputPropsValue, name, updateValue, value]);
    const fieldError =
      customError ||
      (customErrorMatch ? customErrorMatch(errors) : errors[name]);
    const updateValueForField = useCallback(
      (newValue) => {
        updateValue(name, newValue);
      },
      [updateValue, name]
    );
    const i = renderInput({
      fieldQaIdPrefix,
      labelRef,
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ fieldQaIdPrefix: string; label... Remove this comment to see the full error message
      name,
      values,
      value,
      id,
      qaId,
      errors,
      hasError: !!fieldError || hasGeneralError,
      hasSuccess: !!success,
      hasGeneralError: !!generalError && hasGeneralError,
      updateValue: updateValueForField,
      updateValueByName: updateValue,
      updateValueWith: (newValue) => updateValueWith(name, newValue),
      getValue,
      onFocus,
      onBlur,
    });
    return (
      <Box
        position="relative"
        width="100%"
        mb={inlineLabel || hidden || noMargin ? undefined : FIELD_MARGIN}
        className={fieldError ? 'error' : null} // used to scroll to error
      >
        {label ? (
          <FieldLabel
            htmlFor={id}
            // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: true | ReactChild | ReactFragmen... Remove this comment to see the full error message
            ref={labelRef}
            id={`${id}-label`}
            inlineLabel={inlineLabel}
            hasFocus={hasFocus}
            hasValue={isDefined(value)}
            noWrap={noWrap}
          >
            {label}
          </FieldLabel>
        ) : null}
        {description && (
          <Description $noWrap={noWrap}>
            <div>{description}</div>
          </Description>
        )}
        <Box>{i}</Box>
        {fieldError && (
          <ErrorContainer inlineLabel={inlineLabel} noWrap={noWrap}>
            <ErrorIcon />
            <Text pl="4px" data-qa-id={`${qaId}-error`}>
              {mapError(fieldError)}
            </Text>
          </ErrorContainer>
        )}
        {hasGeneralError && generalError && (
          <Error
            inlineLabel={inlineLabel}
            errorMessage={mapError(generalError)}
            qaId={qaId}
            noWrap={noWrap}
          />
        )}
      </Box>
    );
  },
  areEqual
);

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const Field = ({
  name,
  label,
  inlineLabel,
  description,
  renderInput,
  mapError = identity,
  matchGeneralError,
  inputProps = {},
  customCompare,
  customErrorMatch,
  hidden,
  noMargin,
  noWrap,
  success,
  customError,
}: Props<FieldInternalProps>) => {
  const [hasFocus, updateHasFocus] = useState(false);

  const onFocus = () => updateHasFocus(true);

  const onBlur = () => updateHasFocus(false);

  const labelRef = useRef(null);
  const FieldLabel = inlineLabel ? InlineLabel : Label;
  return (
    <FormContextConsumer>
      {({
        values,
        errors,
        getValue,
        updateValue,
        updateValueWith,
        fieldQaIdPrefix,
      }) => (
        <FieldInternal
          values={values}
          errors={errors}
          getValue={getValue}
          updateValue={updateValue}
          updateValueWith={updateValueWith}
          fieldQaIdPrefix={fieldQaIdPrefix || ''}
          inlineLabel={inlineLabel}
          mapError={mapError}
          labelRef={labelRef}
          onFocus={onFocus}
          onBlur={onBlur}
          renderInput={renderInput}
          name={name}
          label={label}
          description={description}
          FieldLabel={FieldLabel}
          hasFocus={hasFocus}
          matchGeneralError={matchGeneralError}
          inputProps={inputProps}
          customCompare={customCompare}
          customErrorMatch={customErrorMatch}
          hidden={hidden}
          noMargin={noMargin}
          noWrap={noWrap}
          customError={customError}
          success={success}
        />
      )}
    </FormContextConsumer>
  );
};

export default Field;
